import { getEntityColor } from '@/components/entity-colors';
import EntityGrid from '@/components/entity-grid/entity-grid';
import FilterBuilder from '@/components/filter-builder/filter-builder';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { type GridQueryOptions } from '@/interfaces';
import type { FilterSettings } from '@/models/filter-settings';
import { entityColumnsQueryOptions } from '@/queries/entityColumnsQueryOptions';
import { moduleDefinitionsQueryOptions } from '@/queries/moduleDefinitionsQueryOptions';
import { useQuery } from '@tanstack/react-query';

import { createFileRoute, Link } from '@tanstack/react-router'
import { EllipsisVerticalIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import SegmentCommunicationActions from '@/components/SegmentCommunicationActions/SegmentCommunicationActions';

export const Route = createFileRoute('/_app/$module_id/$entity_type_id/segments/$segment_id/')({
  component: RouteComponent,
  loader: ({ context: { queryClient }, params }) => {
    return queryClient.ensureQueryData(entityColumnsQueryOptions(params.entity_type_id));
  }
})

function RouteComponent() {
  const { module_id, entity_type_id, segment_id } = Route.useParams();
  const [appliedFilter, setAppliedFilter] = useState<FilterSettings | undefined>(undefined);

  const modulesQuery = useQuery(moduleDefinitionsQueryOptions);
  const modules = modulesQuery.data;
  const moduleDefinition = modules?.find(m => m.id === module_id);
  const currentEntityDefinition = moduleDefinition?.entityDefinitions?.find(e => e.id === entity_type_id);
  const currentSegment = currentEntityDefinition?.segments?.find(s => s.id === segment_id);
  const color = getEntityColor(entity_type_id);

  const { data: columns } = useQuery(entityColumnsQueryOptions(entity_type_id));

  const queryOptions: GridQueryOptions = {
    moduleId: module_id,
    entityTypeId: entity_type_id
  };

  useEffect(() => {
    setAppliedFilter(currentSegment);
  }, [currentSegment, entity_type_id, module_id, segment_id]);

  return (
    <>
      <header
        className={`sticky top-0 flex shrink-0 items-center gap-2 p-2 bg-white border-b-4 border-l-gray-200 border-r-gray-200 border-t-gray-200 border-t-2`}
        style={{
          borderBottomColor: color?.primaryColor
        }}
      >
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink asChild>
                <Link to='/$module_id' params={{ module_id }} search={{
                  sort: [],
                  groupKeys: [],
                  rowGroupCols: [],
                  valueCols: []
                }}>
                  {moduleDefinition?.name}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink asChild>
                <Link to='/$module_id/$entity_type_id/segments/$segment_id' params={{ module_id, entity_type_id, segment_id: 'all' }} search={{
                  sortModel: [],
                  groupKeys: [],
                  rowGroupCols: [],
                  valueCols: []
                }}>
                  {currentEntityDefinition?.name}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbPage>
                {currentSegment?.name ?? "All " + currentEntityDefinition?.name}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-2 ml-auto mr-5">
          <SegmentCommunicationActions
            entityType={entity_type_id}
            segmentId={segment_id}
            className="mr-2"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Actions <EllipsisVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Quick Actions</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to='/$module_id/$entity_type_id/imports' params={{ module_id, entity_type_id }} >
                  Imports
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to='/$module_id/$entity_type_id/segments/manage' params={{ module_id, entity_type_id }} >
                  Manage Segments
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-500">Delete</DropdownMenuItem>

            </DropdownMenuContent>
          </DropdownMenu>



        </div>
      </header>

      <div className="h-full w-full " style={{ backgroundColor: color?.backgroundColor }}>
        {/* {showFilter && <QueryEditor entityTypeId={entity_type_id} filter={currentSegment} applyQuery={setAppliedFilter} />} */}

        <div style={{ width: "calc(100%-40px)", height: "98%", backgroundColor: color?.backgroundColor }} className="m-2">
          <FilterBuilder
            entityTypeId={entity_type_id}
            filter={appliedFilter || currentSegment}
            applyQuery={setAppliedFilter}
            moduleId={module_id}
            currentSegment={currentSegment}
            isAllSegment={segment_id === 'all'}
          />
          <EntityGrid
            moduleId={module_id}
            enableGrouping={true}
            segmentId={segment_id}
            offset={140}
            cols={columns ?? []}
            queryOptions={queryOptions}
            filter={appliedFilter || currentSegment}
            gridState={currentSegment?.gridState}

          />

        </div>
      </div>
    </>
  );
}
